import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search, Archive, Trash2, MailOpen, ChevronDown, Inbox as InboxIcon,
  RotateCcw, CheckCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  bulkInboxAction, fetchInbox, sessionAction, setActiveSession, setView,
} from '../../../store/slices/messengerSlice';

const AVATAR_PALETTE = [
  'bg-blue-200 text-blue-900',
  'bg-teal-200 text-teal-900',
  'bg-amber-200 text-amber-900',
  'bg-purple-200 text-purple-900',
  'bg-rose-200 text-rose-900',
];

const initialsColor = (initials) => {
  const idx = (initials || 'XX').charCodeAt(0) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const days = Math.floor((now - d) / 86400000);
  if (days < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString();
};

// Long-press window for mobile. 500ms feels native (matches Messenger /
// Gmail / iOS) — shorter than that triggers on accidental taps; longer
// frustrates users who expect a quick reveal.
const LONG_PRESS_MS = 500;
// Tolerance in pixels — if the finger drifts more than this during the
// hold, treat it as a scroll/swipe instead and cancel the menu.
const LONG_PRESS_MOVE_TOLERANCE = 12;

const InboxItem = ({ session, active, onSelect, onContextMenu }) => {
  const unread = (session.unread_count || 0) > 0;
  const longPressTimer = React.useRef(null);
  const longPressFiredRef = React.useRef(false);
  const startPosRef = React.useRef({ x: 0, y: 0 });

  const clearLongPressTimer = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    startPosRef.current = { x: t.clientX, y: t.clientY };
    longPressFiredRef.current = false;
    clearLongPressTimer();
    longPressTimer.current = setTimeout(() => {
      longPressFiredRef.current = true;
      // Synthesise a contextmenu-shaped event at the touch position. We use
      // the start position rather than the latest one because the menu is
      // anchored to where the user pressed, not where their finger drifted.
      onContextMenu(
        { preventDefault: () => {}, clientX: startPosRef.current.x, clientY: startPosRef.current.y },
        session,
      );
    }, LONG_PRESS_MS);
  };

  const handleTouchMove = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    const dx = Math.abs(t.clientX - startPosRef.current.x);
    const dy = Math.abs(t.clientY - startPosRef.current.y);
    if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE) {
      clearLongPressTimer();
    }
  };

  const handleTouchEnd = () => clearLongPressTimer();

  const handleClick = () => {
    // Skip the open-thread click if the long-press menu just fired —
    // otherwise the menu pops up and the thread loads at the same time.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onSelect(session.id);
  };

  return (
    <div
      onClick={handleClick}
      onContextMenu={(e) => onContextMenu(e, session)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
      className={`flex gap-3 px-3 py-2.5 cursor-pointer rounded-xl mx-2 transition-colors ${
        active ? 'bg-blue-50' : 'hover:bg-gray-100'
      }`}
    >
      <div className="relative flex-shrink-0">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold ${initialsColor(session.buyer?.avatar_initials)}`}>
          {session.buyer?.avatar_initials || 'GU'}
        </div>
        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-[14px] truncate flex items-center gap-1.5 ${unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-900'}`}>
          <span className="truncate">{session.buyer?.name || 'Buyer'}</span>
          {/* Role of the person messaging — a Collings agent can enquire
              through the portal, so trust the sender's actual CustomUser.role
              rather than assuming every inbound thread is from a buyer. */}
          {(() => {
            const isBuyer = (session.buyer?.role || 'buyer') === 'buyer';
            return (
              <span className={`flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                isBuyer ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {isBuyer ? 'Buyer' : 'Agent'}
              </span>
            );
          })()}
        </div>
        <div className={`text-[12px] truncate mt-0.5 ${unread ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
          {session.last_message_preview || '—'}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <span className={`text-[11px] ${unread ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
          {formatTime(session.last_message_at)}
        </span>
        {unread && (
          <span className="w-2.5 h-2.5 rounded-full bg-blue-600" aria-label={`${session.unread_count} unread`} />
        )}
      </div>
    </div>
  );
};

/**
 * Floating context menu rendered into the inbox area when an item is
 * right-clicked. Items vary by current view — the Archived view swaps in
 * "Unarchive", the Deleted view swaps in "Restore".
 */
const InboxContextMenu = ({ x, y, view, onClose, onRun }) => {
  useEffect(() => {
    const handle = () => onClose();
    document.addEventListener('click', handle);
    document.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    window.addEventListener('blur', handle);
    return () => {
      document.removeEventListener('click', handle);
      document.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
      window.removeEventListener('blur', handle);
    };
  }, [onClose]);

  const W = 210;
  const H = 132;
  const left = Math.min(x, window.innerWidth - W - 8);
  const top = Math.min(y, window.innerHeight - H - 8);

  const item = 'w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-800 hover:bg-gray-100 rounded-md';

  const items = view === 'archived'
    ? [
        { action: 'unarchive', label: 'Unarchive', Icon: RotateCcw },
        { action: 'delete', label: 'Delete', Icon: Trash2 },
        { action: 'mark_unread', label: 'Mark as unread', Icon: MailOpen },
      ]
    : view === 'deleted'
    ? [
        { action: 'restore', label: 'Restore', Icon: RotateCcw },
        { action: 'mark_unread', label: 'Mark as unread', Icon: MailOpen },
      ]
    : [
        { action: 'archive', label: 'Archive', Icon: Archive },
        { action: 'delete', label: 'Delete', Icon: Trash2 },
        { action: 'mark_unread', label: 'Mark as unread', Icon: MailOpen },
      ];

  return (
    <div
      className="fixed z-[10000] bg-white rounded-lg shadow-lg border border-gray-200 py-1.5"
      style={{ left, top, width: W }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map(({ action, label, Icon }) => (
        <button key={action} type="button" className={item} onClick={() => onRun(action)}>
          <Icon size={16} className="text-gray-500" /> {label}
        </button>
      ))}
    </div>
  );
};

/**
 * Header filter dropdown — swaps the inbox view (Inbox / Archived / Deleted)
 * and exposes a bulk "Mark all as read". Mirrors the gear/three-dot menus
 * common at the top of a chat inbox.
 */
const HeaderFilterDropdown = ({ view, onSelectView, onMarkAllRead }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const label = view === 'archived' ? 'Archived'
    : view === 'deleted' ? 'Deleted'
    : 'Inbox';

  const item = (active) => `w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md ${
    active ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-800 hover:bg-gray-100'
  }`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 h-9 px-3 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-medium"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Filter messages"
      >
        <span>{label}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1.5 z-50"
          role="menu"
        >
          <button type="button" className={item(view === 'inbox')} onClick={() => { onSelectView('inbox'); setOpen(false); }}>
            <InboxIcon size={16} /> Inbox
          </button>
          <button type="button" className={item(view === 'archived')} onClick={() => { onSelectView('archived'); setOpen(false); }}>
            <Archive size={16} /> Show archived
          </button>
          <button type="button" className={item(view === 'deleted')} onClick={() => { onSelectView('deleted'); setOpen(false); }}>
            <Trash2 size={16} /> Deleted messages
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button type="button" className={item(false)} onClick={() => { onMarkAllRead(); setOpen(false); }}>
            <CheckCheck size={16} /> Mark all as read
          </button>
        </div>
      )}
    </div>
  );
};

const MessengerInbox = () => {
  const dispatch = useDispatch();
  const { sessions, activeSessionId, unreadTotal, loadingInbox, view } = useSelector((s) => s.messenger);
  const [query, setQuery] = useState('');
  const [menu, setMenu] = useState(null); // { x, y, session }

  const openMenu = (e, session) => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, session });
  };

  const runAction = async (action) => {
    if (!menu?.session) return;
    const sid = menu.session.id;
    setMenu(null);
    try {
      await dispatch(sessionAction({ sessionId: sid, action })).unwrap();
      const labels = {
        archive: 'Archived',
        unarchive: 'Unarchived',
        delete: 'Moved to deleted',
        restore: 'Restored',
        mark_unread: 'Marked unread',
      };
      toast.success(labels[action] || 'Done');
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const handleSelectView = (next) => {
    if (next === view) return;
    dispatch(setView(next));
    // Force an immediate refetch in the new view — the polling tick would
    // pick it up within a few seconds, but the user expects an instant flip.
    dispatch(fetchInbox({ view: next }));
  };

  const handleMarkAllRead = async () => {
    try {
      await dispatch(bulkInboxAction({ action: 'mark_all_read' })).unwrap();
      toast.success('All marked as read');
    } catch (e) {
      toast.error('Failed to mark all as read');
    }
  };

  const filtered = query.trim()
    ? sessions.filter((s) => {
        const q = query.trim().toLowerCase();
        return (
          (s.buyer?.name || '').toLowerCase().includes(q) ||
          (s.last_message_preview || '').toLowerCase().includes(q)
        );
      })
    : sessions;

  return (
    <div className="flex flex-col h-full min-h-0 bg-white border-r border-gray-200 min-w-0">
      <div className="px-4 pt-4 pb-2 flex items-center justify-between flex-shrink-0">
        <div className="min-w-0">
          <div className="text-xl font-bold text-gray-900 leading-tight truncate">
            {view === 'archived' ? 'Archived' : view === 'deleted' ? 'Deleted' : 'Messages'}
          </div>
          <div className="text-[11px] text-gray-500">{unreadTotal} unread</div>
        </div>
        <HeaderFilterDropdown
          view={view}
          onSelectView={handleSelectView}
          onMarkAllRead={handleMarkAllRead}
        />
      </div>
      <div className="px-3 pb-2 flex-shrink-0">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Messenger"
            className="w-full text-sm pl-8 pr-3 py-2 rounded-full bg-gray-100 outline-none focus:bg-white focus:ring-1 focus:ring-blue-300 placeholder:text-gray-400"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto py-1">
        {/* Pinned GeeVee AI assistant — always at the top of the inbox, like
            Meta AI in Messenger. Shown in the main inbox view when not
            searching; selected via the 'geevee' sentinel session id. */}
        {view === 'inbox' && !query.trim() && (
          <div
            onClick={() => dispatch(setActiveSession('geevee'))}
            className={`flex gap-3 px-3 py-2.5 cursor-pointer rounded-xl mx-2 transition-colors ${
              activeSessionId === 'geevee' ? 'bg-blue-50' : 'hover:bg-gray-100'
            }`}
          >
            <div className="relative flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-white overflow-hidden flex items-center justify-center border border-blue-100">
                <img src="/GeeVee-head.svg" alt="GeeVee" className="w-full h-full object-cover" />
              </div>
              <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-medium text-gray-900 truncate flex items-center gap-1.5">
                <span className="truncate">GeeVee</span>
                <span className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#2DC8DB]/15 text-[#1b8fa0]">
                  AI
                </span>
              </div>
              <div className="text-[12px] text-gray-500 truncate mt-0.5">
                Ask me anything about listings &amp; data
              </div>
            </div>
          </div>
        )}
        {loadingInbox && sessions.length === 0 ? (
          <div className="p-4 text-xs text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-xs text-gray-400">
            {query.trim()
              ? 'No matches.'
              : view === 'archived'
              ? 'No archived conversations.'
              : view === 'deleted'
              ? 'No deleted conversations.'
              : 'No conversations yet.'}
          </div>
        ) : (
          filtered.map((s) => (
            <InboxItem
              key={s.id}
              session={s}
              active={s.id === activeSessionId}
              onSelect={(id) => dispatch(setActiveSession(id))}
              onContextMenu={openMenu}
            />
          ))
        )}
      </div>

      {menu && (
        <InboxContextMenu
          x={menu.x}
          y={menu.y}
          view={view}
          onClose={() => setMenu(null)}
          onRun={runAction}
        />
      )}
    </div>
  );
};

export default MessengerInbox;
